import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Users, Search, Megaphone, Lock, ShieldQuestion } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { grupoService, Grupo, GrupoConvitePreview } from '../../services/grupoService';

interface InstanciaOption {
  id: string;
  nome: string;
  tipoApi?: 'UAZAPI' | 'META';
}

interface EntrarGrupoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instancias: InstanciaOption[];
  instanciaPadraoId?: string;
  onEntrou: (grupo: Grupo) => void;
}

export const EntrarGrupoDialog: React.FC<EntrarGrupoDialogProps> = ({
  open, onOpenChange, instancias, instanciaPadraoId, onEntrou,
}) => {
  const { toast } = useToast();
  const [instanciaUazapiId, setInstanciaUazapiId] = useState(instanciaPadraoId || '');
  const [invitecode, setInvitecode] = useState('');
  const [consultando, setConsultando] = useState(false);
  const [entrando, setEntrando] = useState(false);
  const [preview, setPreview] = useState<GrupoConvitePreview | null>(null);

  React.useEffect(() => {
    if (open) {
      setInstanciaUazapiId(instanciaPadraoId || instancias[0]?.id || '');
      setInvitecode('');
      setPreview(null);
    }
  }, [open, instanciaPadraoId, instancias]);

  const handleConsultar = async () => {
    if (!instanciaUazapiId) {
      toast({ title: 'Aviso', description: 'Selecione uma instância', variant: 'destructive' });
      return;
    }
    if (!invitecode.trim()) {
      toast({ title: 'Aviso', description: 'Cole o link ou código de convite', variant: 'destructive' });
      return;
    }
    setConsultando(true);
    setPreview(null);
    try {
      const response = await grupoService.obterInfoConvite(instanciaUazapiId, invitecode.trim());
      setPreview(response.data.grupo);
    } catch (error: any) {
      toast({
        title: 'Convite inválido',
        description: error?.response?.data?.message || 'Não foi possível consultar este convite.',
        variant: 'destructive',
      });
    } finally {
      setConsultando(false);
    }
  };

  const handleEntrar = async () => {
    if (!instanciaUazapiId || !invitecode.trim()) return;
    setEntrando(true);
    try {
      const response = await grupoService.entrarViaConvite(instanciaUazapiId, invitecode.trim());
      toast({ title: 'Você entrou no grupo', description: `"${response.data.grupo?.nome || preview?.nome}" foi adicionado à lista.` });
      onEntrou(response.data.grupo);
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Erro ao entrar no grupo',
        description: error?.response?.data?.message || 'Não foi possível entrar no grupo.',
        variant: 'destructive',
      });
    } finally {
      setEntrando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-white dark:bg-slate-800 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <Users className="h-5 w-5" />
            Entrar em grupo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="dark:text-gray-200" htmlFor="entrar-instancia">Instância</Label>
            <Select value={instanciaUazapiId} onValueChange={setInstanciaUazapiId}>
              <SelectTrigger id="entrar-instancia" className="dark:bg-slate-900 dark:border-slate-700 dark:text-white">
                <SelectValue placeholder="Selecione a instância" />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100">
                {instancias.map((inst) => (
                  <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="dark:text-gray-200" htmlFor="entrar-convite">Link ou código de convite</Label>
            <div className="flex gap-2">
              <Input className="dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
                id="entrar-convite"
                value={invitecode}
                onChange={(e) => { setInvitecode(e.target.value); setPreview(null); }}
                placeholder="https://chat.whatsapp.com/AbCdEfGh..."
              />
              <Button type="button" variant="outline" onClick={handleConsultar} disabled={consultando}>
                {consultando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {preview && (
            <div className="rounded-lg border p-3 space-y-2 dark:border-slate-700">
              <p className="font-medium text-gray-900 dark:text-gray-100">{preview.nome}</p>
              {preview.descricao && (
                <p className="text-xs text-muted-foreground dark:text-gray-400">{preview.descricao}</p>
              )}
              <p className="text-xs text-muted-foreground dark:text-gray-400">{preview.totalParticipantes} participante(s)</p>
              <div className="flex flex-wrap gap-1">
                {preview.isAnnounce && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                    <Megaphone className="h-3 w-3" /> Anúncio
                  </span>
                )}
                {preview.isLocked && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300">
                    <Lock className="h-3 w-3" /> Bloqueado
                  </span>
                )}
                {preview.isJoinApprovalRequired && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                    <ShieldQuestion className="h-3 w-3" /> Aprovação
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={entrando}>Cancelar</Button>
          <Button onClick={handleEntrar} disabled={entrando || !preview} className="bg-green-600 hover:bg-green-700">
            {entrando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Entrar no grupo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EntrarGrupoDialog;
